/**
 * Campana de notificaciones del topbar.
 * - Muestra badge con # de no leídas (polling cada 30s).
 * - Click abre dropdown con últimas 15 notificaciones.
 * - Click en una notif: marca como leída + lleva a la pantalla relevante.
 */
import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";
import { fetchNotifications, fetchUnreadCount, markAllRead, markRead, type Notification } from "../api/notifications";
import type { Route } from "../App";

interface Props {
  go: (r: Route) => void;
  openChat: (userId: number) => void;
}

const POLL_MS = 30_000;

export function NotificationBell({ go, openChat }: Props) {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Polling del unread count
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const n = await fetchUnreadCount();
        if (!cancelled) setUnread(n);
      } catch {
        // silent — no rompe la app si el backend no responde
      }
    };
    tick();
    const interval = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Cargar items al abrir
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchNotifications({ limit: 15 })
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleNotifClick = async (n: Notification) => {
    if (!n.read_at) {
      try {
        await markRead(n.id);
        setUnread((u) => Math.max(0, u - 1));
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
      } catch {
        /* ignore */
      }
    }
    setOpen(false);

    const fromUserId = n.payload && typeof n.payload.from_user_id === "number" ? (n.payload.from_user_id as number) : null;

    switch (n.kind) {
      case "trade_proposal":
      case "trade_accepted":
      case "trade_rejected":
      case "trade_completed":
        go("trades");
        break;
      case "message":
        if (fromUserId) openChat(fromUserId);
        else go("chat");
        break;
      default:
        go("dashboard");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      setUnread(0);
      const now = new Date().toISOString();
      setItems((arr) => arr.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    } catch {
      /* ignore */
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <button
        className="icon-btn notif-bell-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notificaciones${unread ? ` (${unread} sin leer)` : ""}`}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span className="notif-bell-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">
            <span style={{ fontWeight: 700 }}>Notificaciones</span>
            <div style={{ flex: 1 }} />
            {unread > 0 && (
              <button className="link-btn" onClick={handleMarkAllRead}>
                Marcar todas leídas
              </button>
            )}
          </div>

          <div className="notif-dropdown-list">
            {loading && (
              <div className="notif-empty">Cargando…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="notif-empty">
                <Icon name="bell" size={28} />
                <div style={{ marginTop: 8 }}>Sin notificaciones todavía</div>
              </div>
            )}
            {!loading && items.map((n) => (
              <button
                key={n.id}
                className={"notif-item " + (n.read_at ? "" : "unread")}
                onClick={() => handleNotifClick(n)}
              >
                <span className="notif-item-icon">
                  {ICON_BY_KIND[n.kind] ?? "🔔"}
                </span>
                <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                  <div className="notif-item-title">{n.title}</div>
                  {n.body && <div className="notif-item-body">{n.body}</div>}
                  <div className="notif-item-time">{relativeDate(n.created_at)}</div>
                </div>
                {!n.read_at && <span className="notif-dot" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ICON_BY_KIND: Record<string, string> = {
  trade_proposal: "📩",
  trade_accepted: "✅",
  trade_rejected: "❌",
  trade_completed: "🎉",
  message: "💬",
  match: "🔄",
  review: "⭐",
};

function relativeDate(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "ahora";
  if (min < 60) return `hace ${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `hace ${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `hace ${day}d`;
  return d.toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}
